import React, { useEffect, useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { useRuntimeClusters } from './hooks/useRuntimeClusters';
import { buildClusterOptions } from './utils/clusterOptions';
import RuntimeSelectionDisplay from './RuntimeSelectionDisplay';
import RuntimeSelectionInteractive from './RuntimeSelectionInteractive';
import styles from './RuntimeSelection.module.css';

export interface RuntimeSelectionProps {
  flowType?: string;
  connectionType?: string;
  onSelectRuntime: (clusterId: number | string | null | undefined) => void;
  /**
   * When true, renders as a read-only display showing the assigned runtime.
   */
  disabled: boolean;
  selectedClusterId?: string | number | null;
  onVisibilityChange?: (visible: boolean) => void;
}

export default function RuntimeSelection(props: RuntimeSelectionProps) {
  const flowType = props.flowType ?? 'streaming';
  const connectionType = props.connectionType ?? '';
  const { onSelectRuntime, disabled, selectedClusterId } = props;

  const { clusters, isEnabled, isUninitialized, isLoading, isError } = useRuntimeClusters(
    flowType,
    connectionType,
  );

  const options = useMemo(() => buildClusterOptions(clusters), [clusters]);

  const hasSettled = !isUninitialized && !isLoading;
  const willShow = hasSettled && isEnabled && !isError && clusters.length > 1;

  useEffect(() => {
    if (!hasSettled) return;
    props.onVisibilityChange?.(willShow);
    // eslint-disable-next-line
  }, [hasSettled, willShow]);

  useEffect(() => {
    if (flowType !== 'streaming' || !isEnabled) {
      props.onVisibilityChange?.(false);
    }
  }, [flowType, isEnabled, props.onVisibilityChange]);

  if (flowType !== 'streaming') return null;
  if (!isEnabled) return null;
  if (isError) return null;

  if (isUninitialized || isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Skeleton variant="rectangular" height={56} />
      </div>
    );
  }

  if (clusters.length <= 1) return null;

  if (disabled) {
    return <RuntimeSelectionDisplay options={options} selectedClusterId={selectedClusterId} />;
  }

  return (
    <RuntimeSelectionInteractive
      options={options}
      selectedClusterId={selectedClusterId}
      onSelectRuntime={onSelectRuntime}
    />
  );
}
